import { STORAGE_PRESETS, type StoragePresetId } from '@couch/shared';
import { useState } from 'react';
import { StorageKeyPanel } from './StorageKeyPanel';

/** Where to click at each provider: a private bucket and a key that may read and write only it (doc 73). */
const STEPS: Record<StoragePresetId, string> = {
  r2: 'R2 → Create bucket. Then R2 → Manage API tokens → Create API token with Object Read & Write, for this bucket only. The Account ID is on the R2 overview.',
  ovh: 'Public Cloud → Object Storage: create an S3 user, then a Standard bucket in a region, and give the user read and write access to it.',
  hetzner: 'Object Storage → Create bucket in a location (private). Then Security → S3 credentials → Generate credentials.',
  scaleway: 'Object Storage → Create bucket (private). Then IAM → API keys → Generate an API key for Object Storage.',
  backblaze: 'Buckets → Create a Bucket (private). Then Application Keys → Add a New Application Key with access only to this bucket, Read and Write. The region is part of the bucket’s S3 endpoint.',
  gcs: 'Cloud Storage → Create a bucket in a European region. Then Settings → Interoperability → create an HMAC key for a service account that has Storage Object Admin on the bucket.',
  other: 'Create a private bucket and an access key that may read and write it. You need the S3 endpoint (https://…) and the region.',
};

/** Storage: one bucket for the model library and the originals (docs 73–76). */
export function StorageSection() {
  const [preset, setPreset] = useState<StoragePresetId>('r2');
  const p = STORAGE_PRESETS[preset];
  return (
    <section className="panel" aria-labelledby="storage-title">
      <h2 id="storage-title">Storage — a bucket for your models and originals</h2>
      <p>
        Any S3-compatible storage works. Couch keeps models there once, so a new cloud machine fetches them from your bucket instead of the
        internet, and it saves the originals of cloud machines there before the machine is gone. Couch only uses the folder <code>couch/</code> in the bucket.
      </p>
      <label htmlFor="storage-preset">Provider</label>
      <select id="storage-preset" value={preset} onChange={(e) => { setPreset(e.target.value as StoragePresetId); }}>
        {Object.values(STORAGE_PRESETS).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
      <p>{STEPS[preset]}</p>
      <p className="muted">
        {p.egress} Storing costs a few cents per GB and month at most providers — check the price list.
        {p.console && <> <a href={p.console} target="_blank" rel="noopener noreferrer">Open the {p.label} console ↗</a></>}
      </p>
      <StorageKeyPanel preset={preset} />
    </section>
  );
}
